/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { Fragment } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { LEFT_ALIGNMENT, RIGHT_ALIGNMENT, CENTER_ALIGNMENT } from '../../services';
import { resolveWidthAsStyle } from './utils';
import { useIsWithinBreakpoints } from '../../services/hooks/useIsWithinBreakpoints';
export var EuiTableRowCell = ({
  align = LEFT_ALIGNMENT,
  children,
  className,
  truncateText,
  setScopeRow,
  showOnHover,
  textOnly = true,
  hasActions,
  isExpander,
  style,
  width,
  valign = 'middle',
  mobileOptions = {
    show: true
  },
  ...rest
}) => {
  const cellClasses = classNames('euiTableRowCell', {
    'euiTableRowCell--hasActions': hasActions,
    'euiTableRowCell--isExpander': isExpander,
    'euiTableRowCell--hideForDesktop': mobileOptions.only,
    'euiTableRowCell--enlargeForMobile': mobileOptions.enlarge,
    [`euiTableRowCell--${valign}`]: valign
  });
  const contentClasses = classNames('euiTableCellContent', className, {
    'euiTableCellContent--alignRight': align === RIGHT_ALIGNMENT,
    'euiTableCellContent--alignCenter': align === CENTER_ALIGNMENT,
    'euiTableCellContent--showOnHover': showOnHover,
    'euiTableCellContent--truncateText': truncateText,
    // We're doing this rigamarole instead of creating `euiTableCellContent--textOnly` for BWC
    // purposes for the time-being.
    'euiTableCellContent--overflowingContent': textOnly !== true
  });
  const mobileContentClasses = classNames('euiTableCellContent', className, {
    'euiTableCellContent--alignRight': mobileOptions.align === RIGHT_ALIGNMENT || align === RIGHT_ALIGNMENT,
    'euiTableCellContent--alignCenter': mobileOptions.align === CENTER_ALIGNMENT || align === CENTER_ALIGNMENT,
    'euiTableCellContent--showOnHover': mobileOptions.showOnHover ?? showOnHover,
    'euiTableCellContent--truncateText': mobileOptions.truncateText ?? truncateText,
    // We're doing this rigamarole instead of creating `euiTableCellContent--textOnly` for BWC
    // purposes for the time-being.
    'euiTableCellContent--overflowingContent': mobileOptions.textOnly !== true || textOnly !== true
  });
  const childClasses = classNames({
    euiTableCellContent__text: textOnly === true,
    euiTableCellContent__hoverItem: showOnHover
  });
  const widthValue = useIsWithinBreakpoints(['xs', 's']) && mobileOptions.width ? mobileOptions.width : width;
  const styleObj = resolveWidthAsStyle(style, widthValue);

  function modifyChildren(children) {
    let modifiedChildren = children;

    if (textOnly === true) {
      modifiedChildren = <span className={childClasses}>{children}</span>;
    } else if (React.isValidElement(children)) {
      modifiedChildren = React.Children.map(children, child => React.cloneElement(child, {
        className: classNames(child.props.className, childClasses)
      }));
    }

    return modifiedChildren;
  }

  const childrenNode = modifyChildren(children);
  const hideForMobileClasses = 'euiTableRowCell--hideForMobile';
  const showForMobileClasses = 'euiTableRowCell--hideForDesktop';
  const Element = setScopeRow ? 'th' : 'td';
  const sharedProps = {
    scope: setScopeRow ? 'row' : undefined,
    style: styleObj,
    ...rest
  };

  if (mobileOptions.show === false) {
    return <Element className={`${cellClasses} ${hideForMobileClasses}`} {...sharedProps}>
        <div className={contentClasses}>{childrenNode}</div>
      </Element>;
  } else {
    return <Element className={cellClasses} {...sharedProps}>
        {
        /* Mobile-only header */
      }
        {mobileOptions.header && <div className={`euiTableRowCell__mobileHeader ${showForMobileClasses}`}>
            {mobileOptions.header}
          </div>}

        {
        /* Content depending on mobile render existing */
      }
        {mobileOptions.render ? <Fragment>
            <div className={`${mobileContentClasses} ${showForMobileClasses}`}>
              {modifyChildren(mobileOptions.render)}
            </div>
            <div className={`${contentClasses} ${hideForMobileClasses}`}>
              {childrenNode}
            </div>
          </Fragment> : <div className={contentClasses}>{childrenNode}</div>}
      </Element>;
  }
};
EuiTableRowCell.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
     * Vertical alignment of the content in the cell
     */
  valign: PropTypes.any,

  /**
     * Indicates whether the cell should be marked as the heading for its row
     */
  setScopeRow: PropTypes.bool,

  /**
     * Indicates if the column is dedicated to icon-only actions (currently
     * affects mobile only)
     */
  hasActions: PropTypes.bool,

  /**
     * Indicates if the column is dedicated as the expandable row toggle
     */
  isExpander: PropTypes.bool,

  /**
     * Mobile options for displaying differently at small screens;
     * See #EuiTableRowCellMobileOptionsShape
     */
  mobileOptions: PropTypes.shape({
    /**
       * If false, will not render the cell at all for mobile
       */
    show: PropTypes.bool,

    /**
       * Only show for mobile? If true, will not render the column at all for desktop
       */
    only: PropTypes.bool,

    /**
       * Custom render/children if different from desktop
       */
    render: PropTypes.node,

    /**
       * The column's header for use in mobile view (automatically passed down
       * when using `EuiBasicTable`).
       * Or pass `false` to not show a header at all.
       */
    header: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.bool.isRequired]),

    /**
       * Increase text size compared to rest of cells
       */
    enlarge: PropTypes.bool,

    /**
       * Applies the value to the width of the cell in mobile view (typically 50%)
       */
    width: PropTypes.any,

    /**
       * Horizontal alignment of the text in the cell
       */
    align: PropTypes.oneOf(["left", "right", "center"]),

    /**
       * _Should only be used for action cells_
       */
    showOnHover: PropTypes.bool,

    /**
       * Setting `textOnly` to `false` will break words unnecessarily on FF and
       * IE.  To combat this problem on FF, wrap contents with the css utility
       * `.eui-textBreakWord`.
       */
    textOnly: PropTypes.bool,

    /**
       * Don't allow line breaks within cells
       */
    truncateText: PropTypes.bool
  }),

  /**
     * Horizontal alignment of the text in the cell
     */
  align: PropTypes.oneOf(["left", "right", "center"]),

  /**
     * _Should only be used for action cells_
     */
  showOnHover: PropTypes.bool,

  /**
     * Setting `textOnly` to `false` will break words unnecessarily on FF and
     * IE.  To combat this problem on FF, wrap contents with the css utility
     * `.eui-textBreakWord`.
     */
  textOnly: PropTypes.bool,

  /**
     * Don't allow line breaks within cells
     */
  truncateText: PropTypes.bool,
  width: PropTypes.any
};

try {
  EuiTableRowCell.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/table/table_row_cell.tsx',
    description: '',
    displayName: 'EuiTableRowCell',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      valign: {
        defaultValue: {
          value: 'middle'
        },
        description: 'Vertical alignment of the content in the cell',
        name: 'valign',
        parent: {
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"baseline" | "middle" | "top" | "bottom"',
          value: [{
            value: '"baseline"'
          }, {
            value: '"middle"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }]
        }
      },
      setScopeRow: {
        defaultValue: null,
        description: 'Indicates whether the cell should be marked as the heading for its row',
        name: 'setScopeRow',
        parent: {
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      hasActions: {
        defaultValue: null,
        description: 'Indicates if the column is dedicated to icon-only actions (currently\n' + 'affects mobile only)',
        name: 'hasActions',
        parent: {
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isExpander: {
        defaultValue: null,
        description: 'Indicates if the column is dedicated as the expandable row toggle',
        name: 'isExpander',
        parent: {
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      mobileOptions: {
        defaultValue: {
          value: '{\n    show: true,\n  }'
        },
        description: 'Mobile options for displaying differently at small screens;\n' + 'See #EuiTableRowCellMobileOptionsShape',
        name: 'mobileOptions',
        parent: {
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellProps'
        }],
        required: false,
        type: {
          name: 'EuiTableRowCellMobileOptionsShape'
        }
      },
      showOnHover: {
        defaultValue: null,
        description: '_Should only be used for action cells_',
        name: 'showOnHover',
        parent: {
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellSharedPropsShape'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellSharedPropsShape'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      textOnly: {
        defaultValue: {
          value: 'true'
        },
        description: 'Setting `textOnly` to `false` will break words unnecessarily on FF and\n' + 'IE.  To combat this problem on FF, wrap contents with the css utility\n' + '`.eui-textBreakWord`.',
        name: 'textOnly',
        parent: {
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellSharedPropsShape'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellSharedPropsShape'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      truncateText: {
        defaultValue: null,
        description: "Don't allow line breaks within cells",
        name: 'truncateText',
        parent: {
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellSharedPropsShape'
        },
        declarations: [{
          fileName: 'app/src/components/table/table_row_cell.tsx',
          name: 'EuiTableRowCellSharedPropsShape'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'TdHTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'EuiTableRowCellProps', 'EuiTableRowCellSharedPropsShape']
  };
} catch (e) {}